import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../apiSlice";
import { buildQueryString } from "../../utils/buildQueryString";

/* =====================================================
   ENTITY ADAPTER
===================================================== */

const termsAndConditionsAdapter = createEntityAdapter({
  selectId: (item) => item.id,
});

const initialState = termsAndConditionsAdapter.getInitialState();

/* =====================================================
   API SLICE
===================================================== */

export const termsAndConditionsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* =============================
       QUERIES
    ============================= */

    getTermsAndConditionsList: builder.query({
      query: (filters) => buildQueryString("terms_and_conditions", filters),

      transformResponse: (responseData) =>
        termsAndConditionsAdapter.setAll(
          initialState,
          responseData?.data ?? []
        ),

      providesTags: (result) =>
        result
          ? [
              { type: "TermsAndConditions", id: "LIST" },
              ...result.ids.map((id) => ({
                type: "TermsAndConditions",
                id,
              })),
            ]
          : [{ type: "TermsAndConditions", id: "LIST" }],
    }),

    getTermsAndConditionsById: builder.query({
      query: (id) => `terms_and_conditions/${id}`,

      transformResponse: (responseData) => responseData?.data,

      providesTags: (result, error, id) => [
        { type: "TermsAndConditions", id },
      ],
    }),

    /* =============================
       MUTATIONS
    ============================= */

    createTermsAndConditions: builder.mutation({
      query: (body) => ({
        url: "terms_and_conditions",
        method: "POST",
        body,
      }),

      invalidatesTags: [{ type: "TermsAndConditions", id: "LIST" }],
    }),

    updateTermsAndConditions: builder.mutation({
      query: ({ id, body }) => ({
        url: `terms_and_conditions/${id}`,
        method: "PATCH",
        body,
      }),

      invalidatesTags: (result, error, { id }) => [
        { type: "TermsAndConditions", id },
        { type: "TermsAndConditions", id: "LIST" },
      ],
    }),

    deleteTermsAndConditions: builder.mutation({
      query: (id) => ({
        url: `terms_and_conditions/${id}`,
        method: "DELETE",
      }),

      invalidatesTags: (result, error, id) => [
        { type: "TermsAndConditions", id },
        { type: "TermsAndConditions", id: "LIST" },
      ],
    }),
  }),
});

/* =====================================================
   HOOK EXPORTS
===================================================== */

export const {
  useGetTermsAndConditionsListQuery,
  useGetTermsAndConditionsByIdQuery,
  useCreateTermsAndConditionsMutation,
  useUpdateTermsAndConditionsMutation,
  useDeleteTermsAndConditionsMutation,
} = termsAndConditionsApiSlice;

/* =====================================================
   FILTER SELECTOR
===================================================== */

const selectTermsAndConditionsFilter = (state) =>
  state.termsAndConditionsFilter;

/* =====================================================
   QUERY RESULT SELECTOR
===================================================== */

const selectTermsListResult = createSelector(
  [selectTermsAndConditionsFilter, (state) => state],
  (filter, state) =>
    termsAndConditionsApiSlice.endpoints.getTermsAndConditionsList.select(
      filter
    )(state)
);

/* =====================================================
   ENTITY DATA SELECTOR
===================================================== */

const selectTermsListData = createSelector(
  [selectTermsListResult],
  (result) => result?.data ?? initialState
);

/* =====================================================
   ENTITY SELECTORS
===================================================== */

export const {
  selectAll: selectTermsAndConditionsList,
  selectById: selectTermsAndConditionsById,
  selectIds: selectTermsAndConditionsIds,
} = termsAndConditionsAdapter.getSelectors(selectTermsListData);